import { useQuery } from '@apollo/client';
import { LiveTv, SettingsRemote } from '@mui/icons-material';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  CircularProgress,
  Container,
  Typography,
  styled,
  useMediaQuery,
} from '@mui/material';
import { useMemo, useState, type CSSProperties } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  RemoteDocument,
  isUnreachable,
  useSendRemoteCommand,
  useTuneRemoteChannel,
} from '~/api/remote';
import { RemoteControl, remoteAspect } from '~/components/devices';
import { RemoteButton } from '~/generated/graphql';
import logo from '~/images/fc-logo.svg';
import { Channels } from './components/Channels';

// A phone on its side has width to spare and almost no height, so the remote switches to its
// split arrangement there: the same keys, divided into two clusters instead of one column.
// Height-gated so tall landscape screens (tablets, desktop) keep the stacked remote. Kept in the
// `@media` form a style block needs as a key; useMediaQuery strips the prefix itself.
const LANDSCAPE = '@media (orientation: landscape) and (max-height: 600px)';

type Tab = 'channels' | 'remote';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // dvh, not vh: on a phone `100vh` is the *large* viewport, measured as though the
  // address bar were hidden, so filling it overflows whatever is actually on screen.
  // A definite height (not min-height) is what lets the panes below resolve their own
  // height against the space the branding and the tab bar leave over.
  height: '100dvh',
  backgroundColor: theme.palette.background.paper,
}));

// Everything above the tab bar. Claims the leftover height, which is what keeps the bar
// pinned to the bottom of the viewport in both orientations.
const Main = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 auto',
  justifyContent: 'center',
  minHeight: 0,
  // The remote always fits whatever height it is given, so this is only reachable if the
  // branding alone outgrows the viewport.
  overflow: 'auto',
});

const Surface = styled('div')(({ theme }) => ({
  // The page is a flush white surface: no card border, shadow, or outer margin,
  // which is why this is a plain div rather than a Paper. The padding below is
  // the content's own breathing room, not a gutter.
  padding: theme.spacing(3, 2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minHeight: 0,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
  [LANDSCAPE]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const Header = styled('div')({
  // Takes whatever height its content needs — the device name is free to wrap to as
  // many lines as it likes, and the pane below absorbs the difference.
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
});

const Logo = styled('div')(({ theme }) => ({
  width: '75%',
  padding: theme.spacing(0, 0, 2),
  [LANDSCAPE]: {
    maxWidth: 180,
    padding: theme.spacing(0, 0, 1),
  },
}));

const Body = styled('div')({
  // Claims the leftover height. min-height:0 is what allows it to shrink below the
  // remote's natural size rather than overflowing the page.
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  // The remote is sized by height here, not width: it fills whatever vertical space is
  // left and derives its width from the aspect ratio, so it can never push the page
  // taller than the viewport no matter how much branding sits above it. 30rem caps it on
  // desktop. Deliberately no width floor: a floor pins the width while the height stays
  // fixed, so on a short viewport (small phone, an in-app browser's chrome, split screen)
  // the keys spill out of the box and the page scrolls. A small remote beats a clipped one.
  '& .fc-remote-root': {
    height: '100%',
    width: 'auto',
    aspectRatio: '1 / var(--remote-aspect)',
    maxWidth: 'min(100%, 30rem)',
  },
  [LANDSCAPE]: {
    // The split layout is wider than it is tall, so the stacked remote's desktop cap would only
    // shrink it, and its outer margins are height this layout cannot spare.
    '& .fc-remote-root': { marginTop: 0, marginBottom: 0, maxWidth: '100%' },
  },
});

// The grid scrolls on its own rather than taking the page with it, so the branding stays put
// while a fan works through a long guide.
const ChannelBody = styled('div')(({ theme }) => ({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  width: '100%',
  // Keeps the first row of tiles off the device name directly above it.
  paddingTop: theme.spacing(2),
  [LANDSCAPE]: {
    paddingTop: theme.spacing(1),
  },
}));

export const Remote = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const layout = useMediaQuery(LANDSCAPE) ? 'split' : 'stacked';
  const [tab, setTab] = useState<Tab>('remote');

  // The player hands out the token via the remote URL; every request made from
  // this page authenticates with it instead of a user session.
  const context = useMemo(() => ({ headers: { 'x-api-key': token ?? '' } }), [token]);

  const { data, error, loading, refetch } = useQuery(RemoteDocument, {
    context,
    fetchPolicy: 'network-only',
    skip: !token,
  });

  // The token can die while the page is open, and only a press finds out. Refetching swaps the
  // remote for the expired copy below.
  const [sendCommand] = useSendRemoteCommand({ onSessionExpired: () => void refetch() });

  // A stale guide fails the same way, except the fix is a fresh list rather than a fresh token.
  const [tuneChannel] = useTuneRemoteChannel({
    onChannelUnavailable: () => void refetch(),
    onSessionExpired: () => void refetch(),
  });

  const press = (button: RemoteButton) =>
    void sendCommand({ context, variables: { input: { button } } });

  const tune = (channelId: number) =>
    void tuneChannel({ context, variables: { input: { channelId } } });

  // Mute is a pair of buttons here rather than a separate command path: the remote
  // mutation only speaks RemoteButton, and translates these server-side.
  const setMuted = (muted: boolean) => press(muted ? RemoteButton.Mute : RemoteButton.Unmute);

  const remote = token ? data?.remote : null;

  // A missing `remote` means the token is absent, invalid, or expired. A remote
  // that is not realtime means the token is fine but no target can receive a
  // press right now, so the two cases get different messages.
  const controllable = remote != null && remote.realtime;

  // No signal looks identical to a dead token from here, so check before advising a rescan.
  const unreachable = isUnreachable(error);

  // Plenty of shows carry no channel guide at all, and a lone Remote tab is just a strip of
  // chrome over the keys. The tab bar earns its space only once there is somewhere else to go.
  const hasChannels = controllable && remote.channels.length > 0;

  // Derived rather than stored, so a guide that empties out from under an open page (the
  // refetch below) drops the fan back on the remote instead of stranding them on a blank grid
  // with the tab bar gone.
  const pane = hasChannels ? tab : 'remote';

  // The logo urls are presigned and the grid remounts on every visit, so an hour into an open
  // page each visit would request expired links. Refetching re-signs them, and picks up the
  // guide of whatever show a target has switched to since. MUI reports a tap on the current
  // tab as a change too, which would re-download every logo for nothing.
  const selectTab = (next: Tab) => {
    if (next === pane) return;
    setTab(next);
    if (next === 'channels') void refetch();
  };

  // Feeds the ratio the remote is sized by in Body. Assume the tallest remote until the query
  // lands, so the first paint can only ever under-fill, never overflow.
  const aspect = {
    '--remote-aspect': String(
      remoteAspect({
        layout,
        supportsMute: remote?.supportsMute ?? true,
        supportsVolume: remote?.supportsVolume ?? true,
      }),
    ),
  } as CSSProperties;

  return (
    <Root>
      <Helmet title={t('pages.remote.title')} />
      {/* height/flex here keep the definite-height chain intact from Root down to the
          remote — an auto-height link anywhere in between and `height: 100%` stops
          resolving, leaving the remote stuck at its minimum. */}
      <Main>
        <Container
          disableGutters
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxWidth: 444,
            minHeight: 0,
            mx: 'auto',
            [LANDSCAPE]: { maxWidth: 'none' },
          }}
        >
          <Surface style={aspect}>
            <Header>
              <Logo>
                <img alt="FanConnect" src={logo} width="100%" />
              </Logo>
              {/* The device name carries the heading; the page title would only restate
                  what the remote itself makes obvious, and vertical space is scarce here. */}
              {remote && (
                <Typography
                  component="h1"
                  color="text.secondary"
                  variant="h5"
                  fontWeight={'normal'}
                >
                  {remote.deviceName}
                </Typography>
              )}
            </Header>

            {loading ? (
              <CircularProgress aria-label={t('common.status.loading')} sx={{ my: 4 }} />
            ) : !controllable ? (
              <Typography align="center" color="text.secondary" sx={{ my: 2 }}>
                {unreachable
                  ? t('api.remote.no_connection')
                  : remote
                  ? t('pages.remote.offline')
                  : t('pages.remote.expired')}
              </Typography>
            ) : pane === 'channels' ? (
              <ChannelBody>
                <Channels channels={remote.channels} onTune={tune} />
              </ChannelBody>
            ) : (
              <Body>
                <RemoteControl
                  layout={layout}
                  onPress={press}
                  onSetMuted={setMuted}
                  supportsMute={remote.supportsMute}
                  supportsVolume={remote.supportsVolume}
                />
              </Body>
            )}
          </Surface>
        </Container>
      </Main>

      {/* The bar's top line takes the same border grey as the remote's chassis and the channel
          tiles, rather than the fainter `divider` the audio pill uses between its own keys.
          borderTopColor, not borderColor: `borderTop: 1` expands to the `border-top` shorthand,
          which resets the colour to currentColor and would leave this line black. */}
      {hasChannels && (
        <Box sx={{ borderTop: 1, borderTopColor: 'borders.main', flex: '0 0 auto' }}>
          <BottomNavigation
            onChange={(_event, value: Tab) => selectTab(value)}
            showLabels
            sx={{
              bgcolor: 'transparent',
              // A landscape phone has ~400px of height to spend, so the bar gives some back.
              [LANDSCAPE]: { height: 44, '& .MuiBottomNavigationAction-root': { pt: 0.5 } },
            }}
            value={pane}
          >
            <BottomNavigationAction
              icon={<SettingsRemote />}
              label={t('pages.remote.nav.remote')}
              value="remote"
            />
            <BottomNavigationAction
              icon={<LiveTv />}
              label={t('pages.remote.nav.channels')}
              value="channels"
            />
          </BottomNavigation>
        </Box>
      )}
    </Root>
  );
};
